import { z } from 'zod';

import { EButtonHoverType } from '@common/enums';

import { ThemeCssUnit } from '@sites/site-theme/src/enums/css.enum';

import { themeButtonHoverSchema, themeButtonSchema } from '../base';
import type { ThemeButton, ThemeButtonColor, ThemeButtonData } from '../base';
import { toCssSize } from './css-size';

/**
 * A button as the pre-versioning theme stored it: v0's shape plus the two bags it superseded.
 *
 * `styles` was a free-form CSS record and `data` a partial rest-state bag; both were written by
 * panels that predate `base` and both said the same thing it does. They are recognised here — a
 * theme carrying them must still parse — and dropped by `migrateWebsiteThemeButtonLegacy`, because
 * a versioned shape that keeps three spellings of one idea is how the ambiguity survived.
 */
/**
 * A length as the pre-versioning theme stored it: `8`, `'8px'` or `'0'`, depending on the panel.
 * `migrateWebsiteThemeButtonLegacy` is what turns it into `{ value, unit }`.
 */
const legacyCssLengthSchema = z.union([z.string(), z.number()]);

/**
 * A button colour as the pre-versioning theme stored it: the one-element list every writer produces,
 * or a bare string.
 *
 * v0 accepts only the list — the bare form never rendered (see `themeButtonColorSchema`). It is
 * recognised HERE for the same reason every other legacy spelling is: this schema's job is to read
 * what is on disk, and a theme that fails it loses its buttons entirely.
 */
const legacyButtonColorSchema = z.union([z.string(), z.array(z.string())]);

/** The rest state, with its two lengths and its three colours still untyped. */
const themeButtonDataLegacySchema = z.object({
    borderRadius: legacyCssLengthSchema,
    borderStyle: z.string(),
    borderWidth: legacyCssLengthSchema,
    borderColor: legacyButtonColorSchema,
    color: legacyButtonColorSchema,
    background: legacyButtonColorSchema,
});

/**
 * The hover state with the same three colours left untyped.
 *
 * Spelled out rather than inherited from `themeButtonSchema`: that schema's hover is v0's, which
 * now requires lists, so a legacy theme whose hover carries a bare string would fail the parse —
 * and take the whole theme with it.
 */
const themeButtonHoverLegacySchema = themeButtonHoverSchema.extend({
    color: legacyButtonColorSchema,
    background: legacyButtonColorSchema,
    borderColor: legacyButtonColorSchema,
});

/**
 * The contour and corner settings a theme button used to declare.
 *
 * Recognised so a theme carrying them still parses, and typed loosely on purpose: nothing reads
 * them, so pinning their shape would be describing data for the sake of describing it. v0 drops
 * them — see `themeButtonSchema` for why, and for what a theme button would embed instead.
 */
const abandonedButtonSettingsSchema = {
    isIndependentCorners: z.boolean().optional(),
    radiusSettings: z.unknown().optional(),
    advancedBorderSettings: z.unknown().optional(),
};

export const websiteThemeButtonLegacySchema = themeButtonSchema.omit({ base: true, active: true, hover: true }).extend({
    base: themeButtonDataLegacySchema.optional(),
    active: themeButtonDataLegacySchema.optional(),
    hover: themeButtonHoverLegacySchema.optional(),
    /** @deprecated Superseded by `base`. Recognised on read, not carried into v0. */
    styles: z.record(z.string(), z.unknown()).optional(),
    /** @deprecated Superseded by `base`. Recognised on read, not carried into v0. */
    data: themeButtonDataLegacySchema.partial().optional(),
    ...abandonedButtonSettingsSchema,
});

export type WebsiteThemeButtonLegacy = z.infer<typeof websiteThemeButtonLegacySchema>;

/**
 * The hover state an opacity button is supposed to have.
 *
 * `hoverType: 'opacity'` means the button fades on hover — it does not change colour. A stored
 * theme that also carries hover COLOURS for such a button is describing two different effects at
 * once, and the renderer applies the colours, so the button visibly changes hue where the user
 * asked for a fade. The repair is to make the hover colours equal the rest-state ones, which is
 * what "only the opacity changes" means, and to clear any shadow.
 */
function repairOpacityHover(button: WebsiteThemeButtonLegacy): ThemeButton['hover'] {
    const { base, hover } = button;

    // Nothing to derive from. A button with no `base` is one that only ever had the deprecated
    // `styles`/`data` bags; it is left exactly as found rather than guessed at.
    if (!base) {
        return hover && migrateHoverColors(hover);
    }

    if (!hover) {
        return {
            hoverType: EButtonHoverType.opacity,
            opacity: 80,
            shadow: 'none',
            color: toColorList(base.color),
            background: toColorList(base.background),
            borderColor: toColorList(base.borderColor),
        };
    }

    if (hover.hoverType !== EButtonHoverType.opacity) {
        return migrateHoverColors(hover);
    }

    return {
        ...hover,
        color: toColorList(base.color),
        background: toColorList(base.background),
        borderColor: toColorList(base.borderColor),
        shadow: 'none',
    };
}

/**
 * A stored colour as v0 holds it: always a list.
 *
 * A bare string is wrapped rather than dropped — it is a real colour someone chose, and the only
 * thing wrong with it is a spelling that never reached the stylesheet (see `themeButtonColorSchema`
 * for the two silent failures it caused). Wrapping is what makes the theme render for the first
 * time, not a reinterpretation of it.
 */
function toColorList(color: string | string[]): ThemeButtonColor {
    return Array.isArray(color) ? color : [color];
}

/** The hover state's three colours, lifted. Its other fields are already v0's. */
function migrateHoverColors(hover: NonNullable<WebsiteThemeButtonLegacy['hover']>): ThemeButton['hover'] {
    return {
        ...hover,
        color: toColorList(hover.color),
        background: toColorList(hover.background),
        borderColor: toColorList(hover.borderColor),
    };
}

/**
 * A rest state with its lengths typed.
 *
 * A length that parses to nothing falls back to the shipped default rather than being dropped: a
 * button with no `borderWidth` at all renders with the browser's `medium`, which is not what any
 * stored theme meant — unlike a font property, where absent genuinely means "inherit".
 */
function migrateButtonData(base: WebsiteThemeButtonLegacy['base']): ThemeButtonData | undefined {
    if (!base) {
        return undefined;
    }

    return {
        ...base,
        borderRadius: toCssSize(base.borderRadius, ThemeCssUnit.px) ?? { value: 0, unit: ThemeCssUnit.px },
        borderWidth: toCssSize(base.borderWidth, ThemeCssUnit.px) ?? { value: 0, unit: ThemeCssUnit.px },
        color: toColorList(base.color),
        background: toColorList(base.background),
        borderColor: toColorList(base.borderColor),
    };
}

/**
 * Legacy button → v0 button: with its lengths typed, without the bags and settings v0 abandoned,
 * and with its hover state repaired.
 *
 * The repair used to run on EVERY page load, in `utils/adapter.ts`, under a comment reading
 * `//FIX THEME AFTER FIRST VERSION ADAPTER`. Patching stored data on each read is how a shape stays
 * unknowable: nothing on disk ever becomes correct, and the code that fixes it can never be
 * deleted. Doing it here means it happens once, is covered by the migration's tests, and the theme
 * that gets saved afterwards no longer needs it.
 */
export function migrateWebsiteThemeButtonLegacy(button: WebsiteThemeButtonLegacy): ThemeButton {
    const {
        styles: _styles,
        data: _data,
        isIndependentCorners: _isIndependentCorners,
        radiusSettings: _radiusSettings,
        advancedBorderSettings: _advancedBorderSettings,
        base,
        active,
        // Pulled out of `rest` as well, now that the legacy hover is a different type from v0's:
        // spreading it back would carry the untyped colours through and undo the lift below.
        hover: _hover,
        ...rest
    } = button;
    const hover = repairOpacityHover(button);

    return {
        ...rest,
        ...(base ? { base: migrateButtonData(base) } : {}),
        ...(active ? { active: migrateButtonData(active) } : {}),
        ...(hover ? { hover } : {}),
    };
}
